import { Skeleton } from "@solomon-labs/design/components/ui";
import React from "react";

interface USDvSkeletonProps {
    /** Target layout — morphs the outer grid when this changes. */
    targetLayout?: "positions" | "landing";
    /** When true, inner skeleton blocks fade out during the layout morph. */
    exiting?: boolean;
}

/**
 * @version 1.0.0
 * @published 2026-04-18
 * @history
 *   1.0.0 — Initial publish. Loading-state shell for the USDv home
 *           route. Renders a 2-column grid (`3fr_2fr`, ~60/40) that
 *           starts in the has-balance layout — `max-w-5xl` (1024px),
 *           `gap-x-8 gap-y-10` (32px column / 40px row), no horizontal
 *           padding — and morphs to the no-balance layout — full width,
 *           no gap, no outer padding, visible
 *           `border-l` on the right column — when
 *           `targetLayout="landing"`. All outer transitions are CSS
 *           (`max-width`, `padding`, `gap`, `border-color`) on the
 *           compositor thread. Inner blocks cross-fade via `exiting`
 *           so the phase-out hides content during the morph.
 */
export function USDvSkeleton({
    targetLayout = "positions",
    exiting = false
}: USDvSkeletonProps): React.ReactElement {
    const isLanding = targetLayout === "landing";

    const outerSizingClass = isLanding
        ? "max-w-[9999px] gap-0 px-0 pt-0 pb-0"
        : "max-w-5xl gap-x-8 gap-y-10 pt-6 pb-12";

    const rightBorderClass = isLanding
        ? "md:border-l md:border-line-base"
        : "md:border-l md:border-transparent";

    const blocksOpacity = exiting ? "opacity-0" : "opacity-100";

    return (
        <main className="flex-1 bg-surface-ground">
            <div
                className={`mx-auto h-full grid grid-cols-1 md:grid-cols-[3fr_2fr] transition-[max-width,padding,gap] duration-[400ms] ease-[cubic-bezier(0.215, 0.61, 0.355, 1)] ${outerSizingClass}`}
                aria-hidden="true"
            >
                <div
                    className={`flex p-4 md:p-0 transition-opacity duration-[300ms] ease-[cubic-bezier(0.215, 0.61, 0.355, 1)] ${blocksOpacity}`}
                >
                    <Skeleton className="h-full w-full min-h-[600px]" />
                </div>

                <div
                    className={`flex p-4 md:p-6 transition-[border-color,opacity] duration-[400ms] ease-[cubic-bezier(0.215, 0.61, 0.355, 1)] ${rightBorderClass} ${blocksOpacity}`}
                >
                    <Skeleton className="h-full w-full min-h-[600px]" />
                </div>
            </div>
        </main>
    );
}
